import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Eye, EyeOff } from "lucide-react";

const AUTRE = "Autre à préciser";
const EMPTY = {
  statut_juridique: "", statut_juridique_autre: "", raison_sociale: "", siret: "",
  nom: "", prenom: "", email: "", telephone: "", password: "", territoire: "Guadeloupe",
};

function Field({ id, label, children }) {
  return <div className="space-y-2"><Label htmlFor={id}>{label}</Label>{children}</div>;
}

const TERRITOIRES = ["Guadeloupe", "Martinique", "Guyane", "La Réunion", "Mayotte", "Saint-Martin", "Saint-Pierre-et-Miquelon", "Polynésie française", "Nouvelle-Calédonie"];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [statuts, setStatuts] = useState({ predefinis: [], personnalises: [] });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/statuts-juridiques").then((r) => setStatuts(r.data)).catch(() => {});
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    if (!form.statut_juridique) { setError("Choisissez un statut juridique."); setLoading(false); return; }
    try {
      const payload = { ...form, siret: form.siret.replace(/\s/g, "") };
      if (payload.statut_juridique !== AUTRE) delete payload.statut_juridique_autre;
      await register(payload);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 gold-gradient gold-radial">
        <Logo />
        <div className="max-w-md">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#C5A059] mb-3">Rejoignez le groupement</div>
          <h2 className="text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Coopérez, partagez l&apos;emploi, sécurisez les compétences.
          </h2>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Une SCIC SAS au service des entreprises de l&apos;ESS Outre-mer : conventions, missions, paie mutualisée et conformité audit.
          </p>
        </div>
        <div className="text-xs text-slate-500">© 2026 OBJECTIF SCOP OUTREMER</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Créer un compte</h1>
          <p className="text-slate-500 mt-2">Inscrivez votre entreprise pour démarrer.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div className="space-y-2" data-testid="reg-profil-fixed">
              <Label>Profil</Label>
              <div className="h-11 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-sm" data-testid="reg-profil-value">
                Entreprise membre (utilisatrice)
              </div>
            </div>
            <Field id="statut" label="Statut juridique">
              <Select value={form.statut_juridique} onValueChange={(v) => setForm({ ...form, statut_juridique: v })}>
                <SelectTrigger id="statut" data-testid="reg-statut" className="h-11"><SelectValue placeholder="Choisir un statut" /></SelectTrigger>
                <SelectContent>
                  {[...statuts.predefinis, ...statuts.personnalises].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  <SelectItem value={AUTRE}>{AUTRE}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.statut_juridique === AUTRE && (
              <Field id="statut-autre" label="Précisez le statut juridique">
                <Input id="statut-autre" required maxLength={60} value={form.statut_juridique_autre} onChange={set("statut_juridique_autre")} data-testid="reg-statut-autre" className="h-11" />
              </Field>
            )}
            <Field id="raison" label="Raison sociale">
              <Input id="raison" required maxLength={150} value={form.raison_sociale} onChange={set("raison_sociale")} data-testid="reg-raison-sociale" className="h-11" />
            </Field>
            <Field id="siret" label="SIRET (14 chiffres)">
              <Input id="siret" required inputMode="numeric" pattern="[0-9 ]{14,17}" title="14 chiffres" value={form.siret} onChange={set("siret")} data-testid="reg-siret" className="h-11" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field id="nom" label="Nom">
                <Input id="nom" required maxLength={80} autoComplete="family-name" value={form.nom} onChange={set("nom")} data-testid="reg-nom" className="h-11" />
              </Field>
              <Field id="prenom" label="Prénom">
                <Input id="prenom" required maxLength={80} autoComplete="given-name" value={form.prenom} onChange={set("prenom")} data-testid="reg-prenom" className="h-11" />
              </Field>
            </div>
            <Field id="email" label="Email">
              <Input id="email" type="email" required autoComplete="email" value={form.email} onChange={set("email")} data-testid="reg-email" className="h-11" />
            </Field>
            <Field id="telephone" label="Téléphone">
              <Input id="telephone" type="tel" required minLength={6} maxLength={30} autoComplete="tel" value={form.telephone} onChange={set("telephone")} data-testid="reg-telephone" className="h-11" />
            </Field>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} required minLength={6} autoComplete="new-password" value={form.password} onChange={set("password")} data-testid="reg-password" className="h-11 pr-11" />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="reg-toggle-password"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#C5A059] transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Territoire Outre-mer</Label>
              <Select value={form.territoire} onValueChange={(v) => setForm({ ...form, territoire: v })}>
                <SelectTrigger data-testid="reg-territoire" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TERRITOIRES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-testid="reg-error">{error}</div>}

            <Button type="submit" disabled={loading} data-testid="reg-submit" className="w-full h-11 bg-[#C5A059] hover:bg-[#A88846] text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer mon compte"}
            </Button>
            <div className="text-center text-sm text-slate-500">
              Déjà inscrit ?{" "}
              <Link to="/login" data-testid="reg-go-login" className="text-[#C5A059] hover:text-[#A88846] font-medium">Se connecter</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
